import {
  Component, computed, ElementRef, inject, Input, OnInit, Signal, signal,
  ViewChild,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Md5 } from 'ts-md5';
import { Dayjs } from 'dayjs';
import { NgClass } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { User, UserRole } from '../../models/user';
import { AuthService } from '../../services/auth.service';
import { AuthorizationService } from '../../services/authorization.service';
import { KeycloakSessionService } from '../../services/keycloak-session.service';
import { DateTimeFormat } from '../../pipes/date-time.pipe';
import { IMPERSONATION_SESSION_ENDED, IMPERSONATION_SESSION_STORAGE_KEY } from '../../utils/constants.impersonation';
import { NavItem } from '../menu/navItem';
import { NAV_ITEM_SERVICE } from '../../services/navitem.service.interface';
import { TranslatePipe } from '@ngx-translate/core';
import { RouterLinkActive, RouterLink, Router } from '@angular/router';
import { LanguageSwitcherComponent } from '../language-switcher/language-switcher.component';
import { MaintenanceInfoComponent } from '../maintenance-info/maintenance-info.component';
import { OrdinalDatePipe } from '../../pipes/ordinal-date.pipe';
import Keycloak from 'keycloak-js';
import { Select } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { NavigationHistoryService } from '../../services/navigation-history.service';
import { getProfileName } from './header-bar.utils';

@Component({
    standalone: true,
    selector: 'app-header-bar',
    templateUrl: './header-bar.component.html',
    styleUrls: ['./header-bar.component.scss'],
    imports: [
      FormsModule,
      TranslatePipe,
      NgClass,
      RouterLinkActive,
      RouterLink,
      LanguageSwitcherComponent,
      MaintenanceInfoComponent,
      TranslatePipe,
      OrdinalDatePipe,
      Select,
    ],
})
export class HeaderBarComponent implements OnInit {
  @Input()
    title = '';

  @Input()
    backRoute?: string;

  @Input()
    showDefaultBackButton = false;

  @Input()
    defaultBackRoute = '/';

  @Input()
    date?: Dayjs = undefined;

  @Input()
    autoTranslateTitle = true;

  user = signal<User | undefined>(undefined);

  sessionId = signal<string | undefined>(undefined);

  profileImg: string | null = null;

  @ViewChild('sentryButton') sentryReportButton!: ElementRef;

  readonly isImpersonating: boolean;

  protected impersonationSessionEnded = sessionStorage.getItem(IMPERSONATION_SESSION_ENDED) === 'true';

  profileName = computed<string>(() => getProfileName(this.user()));

  organizationName = computed<string>(() => {
    const user = this.user();
    if (user && user.currentOrganization?.name) {
      return user.currentOrganization.name;
    }
    return '';
  });

  visibleNavItems = computed<NavItem[]>(() => {
    const user = this.user();
    return this.navItems().filter((item) => this.isNavItemVisible(item, user));
  });

  private authService = inject(AuthService);
  private authorizationService = inject(AuthorizationService);
  private keycloak = inject(Keycloak);
  private sessionService = inject(KeycloakSessionService);
  private router = inject(Router);
  private navigationHistory = inject(NavigationHistoryService);
  private navService = inject(NAV_ITEM_SERVICE);
  navItems: Signal<NavItem[]>;

  currentOrgId?: string;

  constructor() {
    this.isImpersonating = sessionStorage.getItem(IMPERSONATION_SESSION_STORAGE_KEY) !== null;
    this.navItems = toSignal(this.navService.getHeaderBarNavItems(), { initialValue: [] as NavItem[] });
  }

  ngOnInit(): void {
    this.authService.getCurrentUser().subscribe((user) => {
      this.user.set(user);
      this.profileImg = `https://www.gravatar.com/avatar/${Md5.hashStr(user?.email ?? '')}`;
    });
    this.currentOrgId = this.authService.getActingOrganizationId() ?? undefined;
    const sessionId = this.keycloak.tokenParsed?.['sid'] as string;
    this.sessionId.set(sessionId);
  }

  getOrganizations() {
    return [this.user()!.organization, ...this.user()!.secondaryOrganizations];
  }

  changeOrganization(orgId: string) {
    this.authService.setActingOrganization(orgId);
    this.currentOrgId = orgId;
    // Rebuild page by navigating away and back
    const urlTree = this.router.parseUrl(this.router.url);
    this.router.navigateByUrl('/support', { skipLocationChange: true }).then(() => {
      this.router.navigate([urlTree.root.children['primary']?.segments.map(it => it.path).join('/') || ''], {
        // queryParams: urlTree.queryParams,
      });
    });
  }

  navigateBack() {
    void this.navigationHistory.navigateBack(this.defaultBackRoute);
  }

  async logout() {
    await this.authService.logout();
  }

  async endImpersonation() {
    const exit = () => {
      // Close the current tab
      sessionStorage.removeItem(IMPERSONATION_SESSION_STORAGE_KEY);
      window.close();

      // Reload the current page if closing the tab fails (Scripts may close only the windows that were opened by them.)
      window.location.reload();
    };

    const sessionId = this.sessionId();
    if (sessionId) {
      firstValueFrom(this.sessionService.terminate(sessionId))
        .finally(exit);
    } else {
      exit();
    }
  }

  dismissImpersonationEndedWarning() {
    sessionStorage.removeItem(IMPERSONATION_SESSION_ENDED);
    this.impersonationSessionEnded = false;
  }

  canViewOrganizationSettings(): boolean {
    const user = this.user();
    return !!user && (
      this.authorizationService.hasRole(UserRole.ADMIN, user)
      || this.authorizationService.hasRole(UserRole.MANAGER, user)
    );
  }

  private isNavItemVisible(item: NavItem, user?: User): boolean {
    if (!item.isVisible(user)) {
      return false;
    }

    if (!item.items?.length) {
      return true;
    }

    return item.items.some((nestedItem) => this.isNavItemVisible(nestedItem, user));
  }

  protected readonly DateTimeFormat = DateTimeFormat;
}
